import * as _ from 'lodash';
import * as toastr from 'toastr';

import * as UserManagement from '../../';
import * as Common from '../../../../Common/modules';
import * as Models from '../../../../Models';
import * as Services from '../../../../Services';

export interface IEditGroupScope {
    editGroupCtrl: EditGroupCtrl;
}

export class EditGroupCtrl {
    private showLogging: boolean = false;
    private roleList: Models.Auth.RoleList.RoleList = new Models.Auth.RoleList.RoleList();
    public group: Models.GroupModel.IGroupModel;
    public selectedUsers: UserManagement.models.UsernameResult.IUsernameResult[];
    public roles: Models.RoleModel.IRoleModel[];
    public selectedUserGridHeaders: Common.IgsGrid.Igs.Common.IIgsGridHeader[];
    public loadingUsernames: boolean;
    public noUsernameResults: boolean;
    public userToAdd: string;
    public isSaving = false;
    public lockDelete = false;
    public alerts = <UserManagement.models.Alert.IAlert[]>[];
    public usersInPartner: string[];
    public workIssueNotificationsSuspended: boolean;
    public rejectionEmailDistro: string;
    public cancellationEmailDistro: string;
    public selectedGroupTypeId: number;
    public groupTypes: Models.GroupTypeModel.IGroupTypeModel[];

    public resultsHeaders: Common.IgsGrid.Igs.Common.IIgsGridHeader[] = [
        { name: "Username", property: "username" },
        { name: "Email Address", property: "emailAddress" },
        { name: "Name", property: "displayName" },
        { name: "Agent Code", property: "agentCode" },
        {
            name: "",
            blockSort: true,
            action: {
                name: "View",
                css: "btn btn-default cyan-btn",
                click: (value: any) => {
                    this.$state.go("main.usermanagement.edituser", { id: value.userId });
                },
            },
        },
        {
            name: "",
            blockSort: true,
            action: {
                name: "Remove",
                css: "btn btn-danger",
                click: (value: any) => {
                    this.removeUser(value.userId);
                },
            },
        },
    ];

    public static $inject = [
        "$scope",
        "$stateParams",
        "$timeout",
        "$state",
        "GroupRetrievalService",
        "GroupSaveService",
        "RoleRetrievalService",
        "UserRetrievalService",
        "igs.messageBox",
        "UserService",
        "AuthApiClient",
    ];

    constructor(
        private $scope: IEditGroupScope,
        private $stateParams: any,
        private $timeout: ng.ITimeoutService,
        private $state: angular.ui.IStateService,
        private groupRetrievalService: UserManagement.services.GroupRetrievalService.IGroupRetrievalService,
        private groupSaveService: UserManagement.services.GroupSaveService.GroupSaveService,
        private roleRetrievalService: UserManagement.services.RoleRetrivalService.IRoleRetrievalService,
        private userRetrievalService: UserManagement.services.UserRetrievalService.IUserRetrievalService,
        private messageBox: Common.MessageBox.Igs.Common.IIgsMessageBoxService,
        private userService: Services.UserService.IUserService,
        private authApiClient: Services.AuthApiClient.AuthApiClient,
    ) {
        $scope.editGroupCtrl = this;
        // Only user admins can edit groups
        this.userService.isUserInRole(this.roleList.manageUsersAdmin).then((response) => {
            if (response === false) {
                this.$state.go("main.customersearchv2");
            }
        });

        this.getAndSetGroup()
            .then(() => this.getGroupTypes())
            .then(() => this.getAndSetUsersInGroup())
            .then(() => this.getAndSetUsersInPartner())
            .then(() => this.getAndSetRoles())
            .then(() => this.setLockDelete());
    }

    public saveGroup = () => {
        if (this.showLogging) {
            console.log("saving group...");
        }
        this.isSaving = true;
        this.group.rejectionEmailDistro = this.rejectionEmailDistro;
        this.group.cancellationEmailDistro = this.cancellationEmailDistro;
        this.group.groupTypeId = this.selectedGroupTypeId;
        this.groupSaveService
            .saveGroup(this.group)
            .then(
                () => toastr.success("Group saved successfully", "Success"),
                () => toastr.error("There was a problem saving the group", "Error"),
            )
            .finally(() => {
                this.isSaving = false;
                this.getAndSetUsersInGroup();
                this.setLockDelete();
            });
    };

    public toggleSuspendWorkIssueNotification = () => {
        this.group.suspendWorkIssueNotification = this.workIssueNotificationsSuspended;
    };

    public removeUser = (userId: string) => {
        if (this.showLogging) {
            console.log("removing user...");
        }
        this.selectedUsers = _.filter(this.selectedUsers, (u) => u.userId !== userId);
        this.group.users = _.filter(this.group.users, (u) => u.userId !== userId);
    };

    public addUser = () => {
        if (this.showLogging) {
            console.log("adding user...");
        }
        if (!this.userToAdd) {
            return;
        }

        // get the full user object to load into the grid
        this.userRetrievalService
            .getUserByEmail(<UserManagement.models.UserSearchRequest.IUserSearchRequest>{ userNameOrEmail: this.userToAdd })
            .then((user) => {
                if (user.username != null) {
                    if (user.partner.code !== this.group.partner.code) {
                        toastr.error("User email was not valid", "Error");
                    } else {
                        if (!_.some(this.selectedUsers, { UserId: user.userId })) {
                            this.selectedUsers.push(user);
                        }

                        if (!_.some(this.group.users, (u: Models.UserModel.IUserModel) => u.emailAddress === this.userToAdd)) {
                            this.group.users.push(user);
                        }

                        this.userToAdd = null;
                    }
                } else {
                    toastr.error("User email was not valid", "Error");
                }
            });
    };

    public selectOrUnselectRole = (role: Models.RoleModel.IRoleModel) => {
        if (this.showLogging) {
            console.log("role clicked...");
        }
        if (this.roleIsSelected(role)) {
            this.group.roles = _.filter(this.group.roles, (r) => r.roleId !== role.roleId);
        } else {
            this.group.roles.push(role);
        }
    };

    public getButtonClass = (role: Models.RoleModel.IRoleModel) => {
        return this.roleIsSelected(role) ? "cyan-btn" : "";
    };

    public deleteGroup = () => {
        if (this.showLogging) {
            console.log("deleting group...");
        }
        var confirm = this.messageBox.open("Delete Group", "Are you sure you want to delete this group?", [
            { label: "No", result: false, cssClass: "btn btn-default" },
            { label: "Yes", result: true, cssClass: "btn btn-default cyan-btn" },
        ]);

        confirm.then((result: boolean) => {
            if (result) {
                this.groupSaveService.deleteGroup(this.group.groupId).then(
                    (response: Models.BaseApiResponse.BaseApiResponse) => {
                        if (response.success) {
                            toastr.success("Group Deleted", "Success");
                            this.$state.go("main.usermanagement.search.groups");
                        } else {
                            const errorString = response.errors.reduce((strOne, strTwo) => strOne + strTwo + "\r\n");
                            toastr.error(errorString);
                        }
                    },
                    () => {
                        toastr.error("Could not delete group", "Error");
                    },
                );
            }
        });
    };

    public typeaheadChose = (username: string): void => {
        this.userToAdd = username;
    };

    private roleIsSelected = (role: Models.RoleModel.IRoleModel) => {
        return this.group && _.some(this.group.roles, (r: Models.RoleModel.IRoleModel) => r.roleId === role.roleId);
    };

    private userIsSelected = (user: Models.UserModel.IUserModel) => {
        return this.group && _.some(this.group.users, (u: Models.UserModel.IUserModel) => u.userId === user.userId);
    };

    private setLockDelete = () => {
        if (this.showLogging) {
            console.log("setting lock on delete button...");
        }
        if (this.group) {
            if (this.group.users.length > 0 || this.group.roles.length > 0) {
                this.lockDelete = true;
            } else {
                this.lockDelete = false;
            }
        }
    };

    private getAndSetGroup(): ng.IPromise<void> {
        if (this.showLogging) {
            console.log("getting group...");
        }
        return this.groupRetrievalService
            .getGroupById(this.$stateParams.id)
            .then((group: Models.GroupModel.IGroupModel) => {
                this.group = group;
                this.workIssueNotificationsSuspended = !group.suspendWorkIssueNotification;
                this.rejectionEmailDistro = group.rejectionEmailDistro;
                this.cancellationEmailDistro = group.cancellationEmailDistro;
                this.selectedGroupTypeId = group.groupTypeId;
            });
    }

    private getGroupTypes(): ng.IPromise<void> {
        return this.groupRetrievalService
            .getGroupTypes()
            .then((groupTypes: Models.GroupTypeModel.IGroupTypeModel[]) => {
                this.groupTypes = groupTypes;
            });
    }

    private getAndSetUsersInPartner(): ng.IPromise<void> {
        if (this.showLogging) {
            console.log("getting all user in partner...");
        }
        return this.authApiClient.getUserEmailsByPartner(this.group.partner).then((result: string[]) => {
            this.usersInPartner = _.sortBy(result);
        });
    }

    private getAndSetUsersInGroup(): ng.IPromise<void> {
        if (this.showLogging) {
            console.log("getting users in group...");
        }
        return this.userRetrievalService
            .getUsersInGroup(this.$stateParams.id)
            .then((users: UserManagement.models.UsernameResult.IUsernameResult[]) => {
                this.selectedUsers = _.sortBy(users, (user) => {
                    return user.username.toLowerCase();
                });
            });
    }

    private getAndSetRoles(): ng.IPromise<void> {
        if (this.showLogging) {
            console.log("getting all roles...");
        }
        return this.roleRetrievalService.getAllRoles(true).then((roles: Models.RoleModel.IRoleModel[]) => {
            this.roles = _.sortBy(roles, "name");
        });
    }
}
